import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableHeader,
  TableHeaderCell,
  TableCellLayout,
  Spinner,
  MessageBarBody
} from '@fluentui/react-components';
import { Add24Regular, Delete24Regular, Edit24Regular, Tag24Regular } from '@fluentui/react-icons';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppState } from '../../../../../redux/reducers';
import { ConceptCategory } from '../../../../../models/Entities/Contracts/ConceptCategory';
import ContractConceptCategoryFormComponent from '../ConceptCategory.Form/ContractConceptCategory.Form.Component';
import ConfirmationModalComponent from '../../../../../commons/component/ConfirmationModal/ConformationModal.Component';

// i18Next
import { useTranslation } from 'react-i18next';

// store
import * as ConceptCategoryStore from '../../../../../redux/middleware/ConceptCategoryMiddleware';
import {
  ContractConceptCategoryListProps,
  shouldLoadCategories,
  shouldReloadCategories
} from './ContractConceptCategory.List.Functions';
import { useContractConceptCategoryListStyles } from './ContractConceptCategory.List.Styles';
import { GuideDrawer } from '../../../../../theme/components/GuideDrawer';
import { GuideMessageBar } from '../../../../../theme/components/GuideMessageBar';

export const ContractConceptCategoryListComponent = (props: ContractConceptCategoryListProps) => {
  const [t] = useTranslation();
  const dispatch = useDispatch();
  const styles = useContractConceptCategoryListStyles();

  const conceptCategoryState = useSelector((appState: AppState) => appState.ConceptCategories);

  const [isFormOpen, setIsFormOpen] = useState<boolean>(false);
  const [selectedCategory, setSelectedCategory] = useState<ConceptCategory | undefined>();

  useEffect(() => {
    if (shouldLoadCategories(conceptCategoryState)) {
      dispatch(ConceptCategoryStore.actionCreators.GetAllConceptCategories());
    }
  }, [conceptCategoryState, dispatch]);

  useEffect(() => {
    if (shouldReloadCategories(conceptCategoryState)) {
      dispatch(ConceptCategoryStore.actionCreators.GetAllConceptCategories());
    }
  }, [
    conceptCategoryState?.successAddingNewOne,
    conceptCategoryState?.successUpdatingOne,
    conceptCategoryState?.successDeletingOne,
    conceptCategoryState,
    dispatch
  ]);

  const handleAddNew = () => {
    setSelectedCategory(undefined);
    setIsFormOpen(true);
  };

  const handleEdit = (category: ConceptCategory) => {
    setSelectedCategory(category);
    setIsFormOpen(true);
  };

  const handleFormDismiss = () => {
    setIsFormOpen(false);
    setSelectedCategory(undefined);
  };

  const categories = conceptCategoryState?.list || [];

  return (
    <>
      <GuideDrawer
        open={props.isOpen}
        size="large"
        title={t('contractConceptCategoryList.title')}
        onDismiss={props.onDismiss}
        secondaryAction={{}}
        testId="contract-concept-category-list-drawer"
      >
        <div>
          <Button appearance="primary" icon={<Add24Regular />} onClick={handleAddNew}>
            {t('contractConceptCategoryList.addButton')}
          </Button>
        </div>

        {conceptCategoryState?.isLoadingAll && (
          <div className={styles.loadingContainer}>
            <Spinner label={t('contractConceptCategoryList.loading')} />
          </div>
        )}

        {conceptCategoryState?.failOnLoadingAll && conceptCategoryState?.error && (
          <GuideMessageBar intent="error">
            <MessageBarBody>{conceptCategoryState.error.ErrorMessage}</MessageBarBody>
          </GuideMessageBar>
        )}

        {conceptCategoryState?.isDeletingOne && (
          <GuideMessageBar intent="info">
            <MessageBarBody>{t('contractConceptCategoryList.deleting')}</MessageBarBody>
          </GuideMessageBar>
        )}

        {conceptCategoryState?.successDeletingOne && (
          <GuideMessageBar intent="success">
            <MessageBarBody>{t('contractConceptCategoryList.deleteSuccess')}</MessageBarBody>
          </GuideMessageBar>
        )}

        {conceptCategoryState?.successAddingNewOne && (
          <GuideMessageBar intent="success">
            <MessageBarBody>{t('contractConceptCategoryList.addSuccess')}</MessageBarBody>
          </GuideMessageBar>
        )}

        {conceptCategoryState?.successUpdatingOne && (
          <GuideMessageBar intent="success">
            <MessageBarBody>{t('contractConceptCategoryList.updateSuccess')}</MessageBarBody>
          </GuideMessageBar>
        )}

        {categories.length > 0 && (
          <Table aria-label="Concept Categories">
            <TableHeader>
              <TableRow>
                <TableHeaderCell>{t('contractConceptCategoryList.columnName')}</TableHeaderCell>
                <TableHeaderCell>{t('contractConceptCategoryList.columnDescription')}</TableHeaderCell>
                <TableHeaderCell>{t('contractConceptCategoryList.columnActions')}</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {categories.map((category: ConceptCategory) => (
                <TableRow key={category.id}>
                  <TableCell>
                    <TableCellLayout media={<Tag24Regular />}>
                      {category.conceptCategoryName || category.id}
                    </TableCellLayout>
                  </TableCell>
                  <TableCell>
                    <TableCellLayout>
                      {category.conceptCategoryDescription || '-'}
                    </TableCellLayout>
                  </TableCell>
                  <TableCell>
                    <TableCellLayout>
                      <div className={styles.rowActions}>
                        <Button
                          appearance="subtle"
                          icon={<Edit24Regular />}
                          onClick={() => handleEdit(category)}
                          disabled={conceptCategoryState?.isDeletingOne}
                        >
                          {t('contractConceptCategoryList.editButton')}
                        </Button>
                        <ConfirmationModalComponent
                          onDismiss={() => {}}
                          onConfirm={() => dispatch(ConceptCategoryStore.actionCreators.DeleteConceptCategory(category.id))}
                          title={t('contractConceptCategoryList.deleteButton')}
                          message={t('contractConceptCategoryList.confirmDelete')}
                          buttonApparence="subtle"
                          buttonShape={undefined}
                          buttonTitle={t('contractConceptCategoryList.deleteButton')}
                          buttonIcon={<Delete24Regular />}
                          isProcessing={conceptCategoryState?.isDeletingOne}
                        />
                      </div>
                    </TableCellLayout>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {!conceptCategoryState?.isLoadingAll && categories.length === 0 && (
          <GuideMessageBar intent="info">
            <MessageBarBody>{t('contractConceptCategoryList.noCategories')}</MessageBarBody>
          </GuideMessageBar>
        )}
      </GuideDrawer>

      {isFormOpen && (
        <ContractConceptCategoryFormComponent
          isOpen={isFormOpen}
          dismissPanel={handleFormDismiss}
          ConceptCategory={selectedCategory}
        />
      )}
    </>
  );
};

export default ContractConceptCategoryListComponent;
